import { Link } from 'react-router-dom';
import type { OverviewTriage, PulseIncidents, RevenueIntegrity, TriageFlag, TriageTier } from '@aithora/shared';
import type { QueryState } from '../../useQuery';
import { EmptyState, QueryBlock } from '../../ui/states';
import './triage.css';
import { BuildTagged, useCustomerText } from '../../ui/buildLabels';

// A port of the Console V3 design's Operator Triage section (ovSecIsTriage, ovT*, ovRev*,
// ovPlatformLine, ovLedgerLine in "Aithora ConsoleV3.dc.html"). Class names map to its
// inline styles in triage.css. The queue reads GET /overview/triage and the three money
// figures GET /overview/revenue-integrity; both are PROPOSED.

/** The design's tier inks (TC). */
export const TIER_INK: Record<TriageTier, string> = { HIGH: '#8C3A33', ELEVATED: '#B3261E', WATCH: '#5B6B7C', LOW: '#1F6B52' };
const RANK: Record<TriageTier, number> = { HIGH: 0, ELEVATED: 1, WATCH: 2, LOW: 2 };

/** "10h", "51m", "3d" (the design's fmtAge). */
export function triageAge(iso: string, nowMs: number): string {
  const m = Math.max(0, Math.floor((nowMs - Date.parse(iso)) / 60000));
  return m >= 1440 ? `${Math.floor(m / 1440)}d` : m >= 60 ? `${Math.floor(m / 60)}h` : `${m}m`;
}

/** Highest tier first, unowned before owned, longest waiting first (the design's order). */
export function triageOrder(flags: TriageFlag[]): TriageFlag[] {
  return flags.slice().sort((a, b) => RANK[a.tier] - RANK[b.tier] || (a.owner ? 1 : 0) - (b.owner ? 1 : 0) || Date.parse(a.flaggedAt) - Date.parse(b.flaggedAt));
}

/** The number on the Triage tab: flagged residents nobody owns. */
export function unownedCount(t: OverviewTriage | null): number | null {
  return t ? t.flags.filter((f) => !f.owner).length : null;
}

// Thousands to one decimal, as the design draws them ("$23.5K"); under $1K whole dollars, so zero reads "$0", not "$0.0K".
export const money = (cents: number | null) => (cents === null ? '–' : Math.abs(cents) < 100000 ? `$${Math.round(cents / 100)}` : `$${(cents / 100000).toFixed(1)}K`);

export function OperatorTriage({ q, revenue, incidents }: { q: QueryState<OverviewTriage>; revenue: QueryState<RevenueIntegrity>; incidents: QueryState<PulseIncidents> }) {
  const plain = useCustomerText();
  const nowMs = Date.now();
  const t = q.data;
  const list = t ? triageOrder(t.flags) : [];
  const highUnowned = list.filter((r) => r.tier === 'HIGH' && !r.owner);
  const unowned = list.filter((r) => !r.owner);
  const oldest = unowned.length ? unowned.slice().sort((a, b) => Date.parse(a.flaggedAt) - Date.parse(b.flaggedAt))[0] : null;
  // Nobody scored is not nobody flagged (every detector off), and an unclaimed emergency
  // (the To do list's first rows) leads the verdict whatever the detectors say.
  const scored = !t || t.detectors > 0;
  const unclaimed = t?.emergencies?.unclaimed ?? 0;
  const emergencyLead = unclaimed ? `${unclaimed} ${unclaimed === 1 ? 'emergency' : 'emergencies'} nobody has claimed. ` : '';
  const verdict =
    emergencyLead +
    (!scored
      ? 'No detector is switched on, so nobody is scored for deterioration.'
      : highUnowned.length
        ? `${highUnowned.length}${highUnowned.length === 1 ? ' person is above the high line and nobody owns them.' : ' people are above the high line and nobody owns them.'}`
        : unowned.length
          ? `${unowned.length}${unowned.length === 1 ? ' flagged resident has no owner.' : ' flagged residents have no owner.'}`
          : list.length
            ? 'Everyone flagged today has someone on them.'
            : 'Nobody is flagged today.');
  const verdictTone = unclaimed || highUnowned.length ? 'act' : unowned.length || !scored ? 'watch' : 'good';
  const sub =
    highUnowned.length && oldest
      ? `Longest unowned is ${triageAge(oldest.flaggedAt, nowMs)}, ${oldest.name}, ${oldest.detector.replace(' indicator', '').toLowerCase()}. Claim it or route it.`
      : 'Ownership is the thing that decides whether any of this gets acted on.';
  const counts = [
    { v: String(list.filter((r) => r.tier === 'HIGH').length), k: 'above the high line', tone: list.some((r) => r.tier === 'HIGH') ? 'act' : 'good' },
    { v: String(unowned.length), k: 'with no owner', tone: unowned.length ? 'act' : 'good' },
    { v: oldest ? triageAge(oldest.flaggedAt, nowMs) : '–', k: 'longest unowned', tone: oldest ? 'watch' : 'muted' },
  ];
  const openIncidents = incidents.data ? incidents.data.incidents.filter((i) => i.status !== 'resolved').length : null;

  return (
    <div className="tri" data-testid="overview-triage">
      <div className="tri__min">
        {t ? (
          <div className="tri__top">
            <span className="tri__lead">
              <span className={`tri__verdict tri--${verdictTone}`}>{verdict}</span>
              <span className="tri__sub">{sub}</span>
            </span>
            <span className="tri__counts">
              {counts.map((c) => (
                <span key={c.k} className="tri__count">
                  <span className={`tri__cv tri--${c.tone}`}>{c.v}</span>
                  <span className="tri__ck">{c.k}</span>
                </span>
              ))}
            </span>
          </div>
        ) : null}
      </div>

      <div className="tri__min">
        <div className="tri__head">
          <span className="tri__title">The queue</span>
          <span className="tri__meta">{t ? `${t.flags.length} residents flagged by ${t.detectors} detectors across ${t.facilities} facilities` : ''}</span>
        </div>
        <QueryBlock
          q={q}
          rows={6}
          isEmpty={(d) => d.flags.length === 0}
          empty={
            scored ? (
              <EmptyState compact title="Nobody is flagged right now" body="The eight detectors read each resident against their own baseline." />
            ) : (
              <EmptyState compact title="Nobody is scored: no detector is switched on" body={t?.reason ?? undefined} />
            )
          }
        >
          {() => (
            <div className="tri__rows">
              {list.map((f) => {
                const ink = TIER_INK[f.tier];
                return (
                  <div key={f.id} className="tri__row" style={{ boxShadow: `inset 3px 0 0 ${ink}`, background: f.owner ? undefined : `${ink}0A` }}>
                    <span className="tri__who">
                      <span className="tri__name">{f.name}</span>
                      <span className="tri__room">{`Room ${f.room ?? '–'}`}</span>
                    </span>
                    <span className="tri__tier" style={{ color: ink, background: `${ink}1A` }}>
                      {f.tier}
                    </span>
                    <span className="tri__what">
                      <span className="tri__det">
                        {f.detector} <span className="tri__conf">{`· ${f.confidence} confidence`}</span>
                      </span>
                      <span className="tri__why">{f.why}</span>
                    </span>
                    <span className="tri__age">
                      <span className="tri__agev">{triageAge(f.flaggedAt, nowMs)}</span>
                      <span className="tri__agek">waiting</span>
                    </span>
                    <span className={`tri__owner${f.owner ? '' : ' tri__owner--none'}`}>{f.owner ? f.owner.name : 'No owner'}</span>
                    <Link to="/residents" className={`tri__act${f.owner ? '' : ' tri__act--claim'}`}>
                      {f.owner ? 'Open' : 'Claim'}
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </QueryBlock>
        {t && (
          <div className="tri__note">
            {t.real
              ? 'Each one is a wellness indicator against that resident’s own baseline, not a diagnosis, not a prediction, and never an automatic action.'
              : plain('SAMPLE. The eight detectors are live per resident, but there is no portfolio rollup endpoint yet, so this list is illustrative. Each one is a wellness indicator against that resident’s own baseline, not a diagnosis, not a prediction, and never an automatic action.')}
          </div>
        )}
      </div>

      <div className="tri__min">
        <div className="tri__head tri__head--rev">
          <span className="tri__title">Revenue integrity</span>
          <span className="tri__grow" />
          <Link to="/performance" className="tri__more">
            Open the gap sheet {'→'}
          </Link>
        </div>
        <QueryBlock q={revenue} rows={2}>
          {(d) => (
            <>
              <div className="tri__rev">
                {[
                  { k: 'Measured', v: money(d.measuredCents), note: 'evidence exists · billed against performed', tone: 'ink', pill: null },
                  { k: 'Opportunity', v: money(d.opportunityCents), note: 'never captured · modeled from bed mix', tone: 'watch', pill: d.opportunityBasis === 'modeled' ? 'MODELED' : null },
                  { k: 'Recoverable', v: money(d.recoverableCents), note: 'billed and denied · can be reworked', tone: 'deep', pill: null },
                ].map((m) => (
                  <span key={m.k} className="tri__fig">
                    <span className="tri__figh">
                      <span className="tri__figk">{m.k}</span>
                      {m.pill && <BuildTagged tag={m.pill}><span className="tri__pill">{m.pill}</span></BuildTagged>}
                    </span>
                    <span className={`tri__figv tri--${m.tone}`}>{m.v}</span>
                    <span className="tri__fign">{m.note}</span>
                  </span>
                ))}
              </div>
              <div className="tri__note tri__note--rev">
                {d.real
                  ? 'Three different confidences, three different actions; they are never added into one number. Per-facility sheets are live in Performance → Rebates / Reimbursement.'
                  : 'Three different confidences, three different actions; they are never added into one number. Portfolio figures are modeled until the charge-capture rollup is wired; per-facility sheets are live in Performance → Rebates / Reimbursement.'}
              </div>
            </>
          )}
        </QueryBlock>
      </div>

      <div className="tri__foot">
        <Link to="/pulse" className="tri__line">
          {t?.platform && openIncidents !== null
            ? `Platform ${t.platform.healthy ? 'healthy' : 'not healthy'} · ${openIncidents} open incidents · ${t.platform.guardsNearTrip} guards within 15% of trip`
            : openIncidents !== null
              ? `${openIncidents} open incidents`
              : 'System Pulse'}{' '}
          {'→'}
        </Link>
        <Link to="/oversight" className="tri__line">
          {t?.ledger
            ? t.ledger.verifiedToHead && t.ledger.breaks === 0
              ? 'Truth Ledger verified to head · no breaks in this tenant’s chain'
              : `Truth Ledger · ${t.ledger.breaks} ${t.ledger.breaks === 1 ? 'break' : 'breaks'} in this tenant’s chain`
            : 'Truth Ledger'}{' '}
          {'→'}
        </Link>
      </div>
    </div>
  );
}
